import commonConstants from './commonConstants';
import { filterUnique } from './utils';

/**
 * Return the first and last name of a guest
 * If NULL is passed, it's replaced by an empty string
 * If no name can be found "Unknown Guest" is returned
 *
 * @param { firstName?: string, lastName?: string } guest
 * @param {fallback?: string} opts
 * @returns string
 */
export const formatGuestName = (guest, opts) => {
  const { firstName, lastName } = guest;
  const { fallback = commonConstants.UNKNOWN_GUEST, order } = opts ?? {};
  let { separatorChar = ' ' } = opts ?? {};

  if (!(firstName && lastName)) {
    separatorChar = '';
  }
  const name =
    order && order === 'LastFirst'
      ? `${lastName ?? ''}${separatorChar}${firstName ?? ''}`
      : `${firstName ?? ''}${separatorChar}${lastName ?? ''}`;
  return !name ? fallback : name.trim();
};

export const formatBookingGuests = (bookingGuests) =>
  bookingGuests.map(({ id, ...guest }) => ({ value: id.toString(), label: formatGuestName(guest) }));

/**
 * Takes a string, and returns the first name and last name
 * @param {String} fullName
 */
export const parseFullGuestName = (fullName) => {
  if (fullName?.length === undefined) return { firstName: '', lastName: '' };
  const trimmedFullName = fullName.trim();
  const firstNameEndIndex = trimmedFullName.indexOf(' ') === -1 ? trimmedFullName.length : trimmedFullName.indexOf(' ');
  const firstName = trimmedFullName.substring(0, firstNameEndIndex).trim();
  const lastName = trimmedFullName.substring(firstNameEndIndex + 1).trim();
  return {
    firstName,
    lastName
  };
};

const formatBookingGuestsForRoomNightDays = (bookingGuests = []) =>
  bookingGuests.map((bookingGuest) => {
    const { id, firstName = '', lastName = '' } = bookingGuest;

    return { id, guestName: formatGuestName({ firstName, lastName }, { fallback: '' }) };
  });

export const formatRoomNightDays = (roomNights = []) =>
  roomNights.map((roomNight) => {
    const { id: roomNightId, date, bookingGuests = [], room = {}, costTrackingCode = {}, state } = roomNight;
    const { id: roomId } = room || {};
    const { id: costTrackingCodeId } = costTrackingCode || {};

    return {
      id: roomNightId,
      date,
      bookingGuests: bookingGuests?.length ? formatBookingGuestsForRoomNightDays(bookingGuests) : [],
      costTrackingCodeId,
      roomId,
      state
    };
  });

/**
 * Formats guest options for booking.
 *
 * This function takes an array of booking guests and formats them into an object
 * where each key is a comma-separated list of guest IDs, and the value is an object
 * containing the guest IDs, a label with room index and guest names, and the guest names.
 *
 * @param {Array} allBookingGuests - An array of arrays, where each inner array contains guest objects for a room.
 * @param {Object[]} allBookingGuests[].guest - An object representing a guest.
 * @param {number} allBookingGuests[].guest.roomIndex - The index of the room the guest is in.
 * @param {string} allBookingGuests[].guest.id - The ID of the guest.
 * @param {string} allBookingGuests[].guest.name - The name of the guest.
 * @returns {Object[]} An array of formatted guest options, sorted by the label.
 *
 * E.X.
 * [
      {
        value: '814583,814584',
        label: 'Room 1: Ashish Ambekar, and Venkata Balabhadra',
        guestName: ['Ashish Ambekar', 'Venkata Balabhadra']
      },
      {
        value: '814473,814479',
        label: 'Room 1: Chris Copeland, and N/A',
        guestName: ['Chris Copeland', 'N/A']
      }
    ];
 */
export const formatGuestOptions = (allBookingGuests) => {
  const result = allBookingGuests.reduce((acc, eachRoomGuests = []) => {
    const guestIds = eachRoomGuests.map((guest) => guest.id).join(',');
    if (guestIds in acc) return acc;

    const roomIndex = eachRoomGuests[0]?.roomIndex;
    const guestNames = eachRoomGuests.map((guest) => formatGuestName(guest));
    const guestNames2 = eachRoomGuests.reduce((formattedGuestName, guest, index, array) => {
      const name = formatGuestName(guest);
      if (index === 0) return name;
      if (index === array.length - 1) return `${formattedGuestName}, and ${name}`;
      return `${formattedGuestName}, ${name}`;
    }, '');

    acc[guestIds] = {
      value: guestIds,
      label: `Room ${roomIndex}: ${guestNames2}`,
      guestName: guestNames,
      roomIndex
    };

    return acc;
  }, {});

  return Object.values(result).sort((a, b) => a.label.localeCompare(b.label));
};

/**
 *
 * T
 * @param {*} bookingGuests
 * @returns
 * E.X.
 * [
 *   { "value": 2, "label": "Room 2: Carla Jones, " },
 *   { "value": 1, "label": "Room 1: Chris Copeland, Unknown Guest, Ashish Ambekar, and 3 more" }
 * ]
 */
export const formatGuestForRoomSwitcher = (bookingGuests) =>
  bookingGuests
    .reduce((allRooms, guest) => {
      const { roomIndex: guestRoomIndex } = guest;
      const fullName = formatGuestName(guest);

      let index = allRooms.findIndex(({ value }) => value === guestRoomIndex);
      if (index === -1) index = allRooms.length;
      const { label: existingLabel = [] } = allRooms[index] ?? {};

      const updatedRooms = allRooms;
      updatedRooms[index] = {
        value: guestRoomIndex,
        label: [`Room ${guestRoomIndex}:`, ...existingLabel, fullName]
      };
      return updatedRooms;
    }, [])
    .map(({ value, label }) => {
      const uniqueLabel = label.filter(filterUnique);
      const moreGusets = uniqueLabel.length > 4 ? `, and ${uniqueLabel.length - 4} more` : '';
      return {
        value,
        label: uniqueLabel.slice(0, 4).join(', ').replace(':,', ':').concat(moreGusets)
      };
    })
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
